import { Link } from "react-router-dom";
import { useUiLang } from "../../../stores/uiLang";

// Run E2 J2: support hub in English and Spanish. "Guided Training" (PFL tours:
// FAFSA, W-4, Career Path) is hidden and its route redirects to the Help
// Center. The Live Chat card said "a real person ... 8am-5pm CST", but the
// support chat is an AI assistant (ChatPage calls the "openai" function).
const SUPPORT_T = {
  en: {
    title: "Need Help? We're Here for You.",
    intro: "Guides and support for teachers using Project Explore.",
    cards: [
      { to: "/support/help-center", icon: "help", title: "Help Center", text: "Short how-to guides for teachers.", cta: "Visit Help Center" },
      { to: "/support/chat", icon: "chat", title: "Live Chat", text: "Ask our AI assistant questions about using Project Explore.", cta: "Start Chat" },
      { to: "/support/feedback", icon: "feedback", title: "Feedback & Suggestions", text: "Tell us what works for you and what we can improve.", cta: "Submit Feedback" },
    ],
    stillTitle: "Still Need Help?",
    stillText: "If you can't find what you're looking for, please contact our team.",
    stillCta: "Contact Support",
  },
  es: {
    title: "¿Necesitas ayuda? Aquí estamos.",
    intro: "Guías y ayuda para maestros que usan Project Explore.",
    cards: [
      { to: "/support/help-center", icon: "help", title: "Centro de ayuda", text: "Guías breves para maestros.", cta: "Ir al Centro de ayuda" },
      { to: "/support/chat", icon: "chat", title: "Chat", text: "Hazle preguntas a nuestro asistente de IA sobre cómo usar Project Explore.", cta: "Empezar el chat" },
      { to: "/support/feedback", icon: "feedback", title: "Comentarios y sugerencias", text: "Cuéntanos qué te funciona y qué podemos mejorar.", cta: "Enviar comentarios" },
    ],
    stillTitle: "¿Todavía necesitas ayuda?",
    stillText: "Si no encuentras lo que buscas, por favor comunícate con nuestro equipo.",
    stillCta: "Contactar a soporte",
  },
};

const ICONS: Record<string, string> = {
  help: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
  chat: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z",
  feedback: "M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z",
};
const ARROW = "M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z";

export default function SupportPage() {
  const t = SUPPORT_T[useUiLang()];
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-covers py-16 text-center px-4">
        <h1 className="text-4xl cover-primary-text font-bold mb-4">{t.title}</h1>
        <p className="text-lg cover-secondary-text max-w-2xl mx-auto">{t.intro}</p>
      </section>

      <section className="py-12 px-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {t.cards.map((card) => (
            <div
              key={card.to}
              className="bg-white rounded-lg shadow-md p-6 border border-gray-200"
            >
              <div className="flex items-start">
                <div className="mr-4 icon-color">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={ICONS[card.icon]}
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold primary-text mb-2">
                    {card.title}
                  </h2>
                  <p className="secondary-text mb-3">{card.text}</p>
                  <Link
                    to={card.to}
                    className="icon-color hover:opacity-80 font-medium inline-flex items-center"
                  >
                    {card.cta}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 ml-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path fillRule="evenodd" d={ARROW} clipRule="evenodd" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-12 px-6 text-center bg-gray-100">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold primary-text mb-4">{t.stillTitle}</h2>
          <p className="secondary-text mb-6">{t.stillText}</p>
          <Link to="/contact-support" className="btn btn-small max-w-[200px] mx-auto">
            {t.stillCta}
          </Link>
        </div>
      </section>
    </div>
  );
}
